<template>
  <main :class="['category', {cols_2: hasMultipleDisplayColumns}]">
    <section v-if="totalCategories > 0">
      <h1>{{ $_translations.value.page_select_category_game_title }}</h1>

      <template v-if="hasMultipleDisplayColumns">
        <div class="flex-row">
          <div
            v-for="(columnCategoryList, index) in categoryListSplitByDisplayColumns"
            :key="index"
            class="flex-column"
          >
            <CategoryItem
              v-for="category in columnCategoryList"
              :key="category.id"
              :category="category"
              @select:category="handleCategorySelect"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <CategoryItem
          v-for="category in categoryList"
          :key="category.id"
          :category="category"
          @select:category="handleCategorySelect"
        />
      </template>
    </section>
  </main>

  <PreGameSection
    v-if="isPreGameVisible"
    @hide:preGame="hidePreGame"
  />

  <MaintenanceModal
    v-else-if="isMaintenanceModalOpen"
    @redirect:homePage="handleRedirectToHomePageFromMaintenanceModal"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import MaintenanceModal from '@/components/modal/MaintenanceModal.vue';
import PreGameSection from '@/components/section/PreGameSection.vue';
import CategoryItem from '@/views/game/components/item/CategoryItem.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { apiRetrieveGameCategories } from '@/services/game.js';
import { gameCategoryEnum, gameThemeEnum } from '@/constants/game.js';

const COLUMN_SPLIT_THRESHOLD = 6;
const TOTAL_DISPLAY_COLUMNS = 2;

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isMaintenanceModalOpen, openMaintenanceModal, closeMaintenanceModal] = useModalHook();
const { isPreGameVisible, setGameTypeAndStartPreGame, hidePreGame } = usePreGameHook();

const categoryList = ref([]);

const totalCategories = computed(() => categoryList.value.length);

const hasMultipleDisplayColumns = computed(() => totalCategories.value > COLUMN_SPLIT_THRESHOLD);

const categoryListSplitByDisplayColumns = computed(() => {
  const categoriesPerColumn = Math.ceil(totalCategories.value / TOTAL_DISPLAY_COLUMNS);

  return [...Array(TOTAL_DISPLAY_COLUMNS).keys()].reduce((acc, columnIndex) => {
    acc.push(categoryList.value.slice(columnIndex * categoriesPerColumn, (columnIndex + 1) * categoriesPerColumn));

    return acc;
  }, []);
});

const setCategoryList = (list) => {
  categoryList.value = list.map((category) => ({
    id: category,
    name: `page_select_category_game_category_name_${ category }`,
  }));
};

const handleCategorySelect = (categoryId) => {
  setGameTypeAndStartPreGame(categoryId);
};

const handleRedirectToHomePageFromMaintenanceModal = async () => {
  await closeMaintenanceModal({ immediate: true });

  redirectToHomePage();
};

const retrieveCategories = async () => {
  try {
    const queryParams = {
      msisdn: authStore.user.msisdn,
    };

    const { parsedData } = await apiRetrieveGameCategories({ queryParams });

    if (parsedData.length === 1) {
      setGameTypeAndStartPreGame(parsedData[0]);
    } else {
      setCategoryList(parsedData);
    }
  } catch {
    openMaintenanceModal({ hideImmediate: true, afterHideCallback: redirectToHomePage });
  }
};

onMounted(() => {
  if (appSettingsStore.appProperties.gameTheme === gameThemeEnum.ALL) {
    setCategoryList(Object.values(gameCategoryEnum));
  } else if (appSettingsStore.appProperties.gameTheme === gameThemeEnum.SEASONAL) {
    retrieveCategories();
  } else {
    redirectToHomePage();
  }
});
</script>
